import { FaRegChartBar } from "react-icons/fa";
import { BsQuestionSquare } from "react-icons/bs";

const generateBlurb = (text, wide = false) => (
  <span className="tile-blurb__wrapper">
    <BsQuestionSquare className="tile-blurb__icon" />
    <span
      className={`tile-blurb__content ${
        wide ? "tile-blurb__content-wide" : ""
      }`}
    >
      {text}
    </span>
  </span>
);

const generateLegend = (content) => (
  <span className="tile-legend__wrapper">
    <FaRegChartBar className="tile-legend__icon" />
    <span className="tile-legend__content">{content}</span>
  </span>
);

export const emissionFootprintTile = {
  blurb: generateBlurb(
    "Sum of scope 1 emissions (generated from your fleet), scope 2 emissions " +
      "(generated from purchased electricity), and scope 3 category 3 emissions " +
      "(generated from the upstream emissions of fuel consumed by company owned " +
      "and operated vehicles). Keep in mind you must enter your monthly utility " +
      "information into Scope 2 Manager to populate scope 2 emissions." +
      " GreenIRR's platform does not currently support methanol due to its limited use in the trucking industry and the outdated nature of the most recent CO₂ emissions factor, which dates back to 2014. To maintain reporting accuracy and align with current industry standards, methanol has been excluded from our list of supported fuel types."
  ),
  legend: "",
};

export const industryCarbonRatingTile = {
  blurb: generateBlurb(
    "0-100 rating that indicates how you perform against for-hire and private " +
      "carrier members of the EPA SmartWay programme. This compares your monthly " +
      "emissions performance against the 3,092 members."
  ),
  legend: generateLegend(
    <>
      <div className="industry-carbon-rating-legend-group">
        <div className="industry-carbon-rating-legend-subgroup">
          <div className="icr-legend-subgroup-red" />
          <div>Significantly Poor Emissions Performance</div>
        </div>
        <div className="industry-carbon-rating-legend-subgroup">
          <div className="icr-legend-subgroup-orange" />
          <div>Poor Emissions Performance</div>
        </div>
        <div className="industry-carbon-rating-legend-subgroup">
          <div className="icr-legend-subgroup-yellow" />
          <div>Average Emissions Performance</div>
        </div>
        <div className="industry-carbon-rating-legend-subgroup">
          <div className="icr-legend-subgroup-ygreen" />
          <div>Above-Average Emissions Performance</div>
        </div>
        <div className="industry-carbon-rating-legend-subgroup">
          <div className="icr-legend-subgroup-green" />
          <div>Significantly Above-Average Emissions Performance</div>
        </div>
      </div>
    </>
  ),
};

export const costOfCarbonTile = {
  blurb: generateBlurb(
    "This figure tells you the cost of every metric ton of CO₂ you emit. It " +
      "is calculated by taking your monthly fleet emissions and dividing it by " +
      "your monthly spend on fuel from fuel-card purchases."
  ),
  legend: "",
};

export const fuelConsumptionTile = {
  blurb: generateBlurb(
    "This figure tells you the amount of fuel your fleet consumes per mile. " +
      "It is calculated by taking your monthly fleet fuel spend and dividing it " +
      "by your monthly fleet mileage."
  ),
  legend: "",
};

export const vehicleEmissionsTile = {
  blurb: generateBlurb(
    "Top eight highest-emitting vehicles based upon the kilograms of CO₂e " +
      "they emit per mile."
  ),
  legend: generateLegend(
    <>
      <p>The industry benchmark is 1-3.5 kg CO₂e / mile.</p>
      <div className="vehicle-emissions-legend-subgroup-wrapper">
        <div className="vehicle-emissions-legend-subgroup">
          <div className="vehicle-emissions-legend-benchmark-green" />
          <div>Below Benchmark</div>
        </div>
        <div className="vehicle-emissions-legend-subgroup">
          <div className="vehicle-emissions-legend-benchmark-red" />
          <div>Above Benchmark</div>
        </div>
      </div>
    </>
  ),
};

// ------------------------------ Emissions page ----------------------------- //

export const scope1ChartEmissionsTile = {
  blurb: generateBlurb(
    "Scope 1 emissions are generated by your trucking fleet. This chart " +
      "displays your monthly fleet emissions over the past year — hover over a " +
      "datapoint to view that month's emissions."
  ),
  legend: generateLegend(
    "The Industry Benchmark is calculated by multiplying the DOE's monthly " +
      "average emissions for an HDV by the number of active vehicles in your " +
      "fleet for the month. You can add it to the chart or take it away by " +
      "clicking it."
  ),
};

export const scope1CardEmissionsTile = {
  blurb: generateBlurb(
    "The current month's scope 1 emissions. Below that is the increase or " +
      "decrease in emissions from the previous month." +
      " GreenIRR's platform does not currently support methanol due to its limited use in the trucking industry and the outdated nature of the most recent CO₂ emissions factor, which dates back to 2014. To maintain reporting accuracy and align with current industry standards, methanol has been excluded from our list of supported fuel types."
  ),
  legend: "",
};

export const scope2ChartEmissionsTile = {
  blurb: generateBlurb(
    "Scope 2 emissions are generated from purchased electricity, which is " +
      "directly related to utilities or electric-vehicle charging. You must " +
      "enter monthly utility-bill information in Scope 2 Manager for data to " +
      "be available."
  ),
  legend: "",
};

export const scope2CardEmissionsTile = {
  blurb: generateBlurb(
    "The current month's scope 2 emissions. Below that is the increase or " +
      "decrease in emissions from the previous month."
  ),
  legend: "",
};

export const lifecycleEmissionsTile = {
  blurb: generateBlurb(
    "Lifecycle emissions include both the fossil-fuel and biogenic portions " +
      "across all three lifecycle stages — WTT, TTW and WTW. Biogenic emissions " +
      "are not reported separately from fossil-fuel emissions due to the " +
      "aggregated nature of GLEC emission factors, which are expressed in CO₂e " +
      "rather than individual gas-specific factors. However, in Scope 1 and " +
      "Scope 2 emissions, the biogenic portion is reported separately. For " +
      "Scope 3 emissions GreenIRR reports both fossil and biogenic CO₂ portions " +
      "of upstream emissions separately. However, due to the aggregated nature " +
      "of GLEC's emission factors, complete disaggregation of biofuel CO₂ " +
      "emissions is not feasible. Therefore, GreenIRR reports the fossil portion " +
      "of these emissions under Scope 3 and the biogenic portion independently " +
      "in the Biogenic Emissions scope, using units of CO₂e. This method aligns " +
      "with GLEC's best-practice guidelines and ensures transparency in " +
      "upstream biofuel emissions reporting." +
      " GreenIRR's platform does not currently support methanol due to its limited use in the trucking industry and the outdated nature of the most recent CO₂ emissions factor, which dates back to 2014. To maintain reporting accuracy and align with current industry standards, methanol has been excluded from our list of supported fuel types.",
    true
  ),
  legend: "",
};

// ------------------------------ Reduction page ---------------------------- //

export const emissionReductionTargetTile = {
  blurb: generateBlurb(
    "You can set your target by selecting your percent reduction from the " +
      "drop-down and your target year. The light bars represent the threshold " +
      "you are ‘allowed' to emit to stay on track for the target, and the dark " +
      "bars represent your actual monthly emissions. To stay on track you want " +
      "your performance to be at or below the threshold."
  ),
  legend: "",
};

export const forecastingChartTile = {
  blurb: generateBlurb(
    "Future forecast of monthly emissions based upon monthly historical " +
      "emissions."
  ),
  legend: "",
};

// ----------------------- Vehicle-performance section ---------------------- //

export const emissionsPerVehicleTile = {
  blurb: generateBlurb(
    "Emissions are broken down on a vehicle level based on active fuel " +
      "consumption of each vehicle while driving. These are fossil-fuel CO₂ " +
      "emissions only and do not include CH₄ or N₂O emissions. Vehicle-level " +
      "biogenic emissions are reported separately under Biogenic Emissions in " +
      "the report. The vehicle IDs match those of your telematics provider."
  ),
  legend: generateLegend(
    "Industry benchmark is 10.16 in alignment with the DOE's monthly average " +
      "for HDV."
  ),
};

export const avgEmissionsPerVehicleTile = {
  blurb: generateBlurb(
    "Amount of CO₂ emissions per mile emitted on a vehicle level, taken as an " +
      "average of all vehicles in the fleet. This is calculated each month by " +
      "taking the average emissions of each vehicle in your fleet and dividing " +
      "it by the average miles driven by each vehicle in that month. These are " +
      "fossil-fuel CO₂ emissions only and do not include CH₄ or N₂O emissions. " +
      "Vehicle-level biogenic emissions are reported separately under Biogenic " +
      "Emissions in the report and are not included in this calculation."
  ),
  legend: generateLegend("Emissions should be within 1,000-2,500 g CO₂/mile."),
};

export const totalIdleTimeTile = {
  blurb: generateBlurb(
    "Total number of hours that vehicles idled in the current month."
  ),
  legend: "",
};

export const totalIdleTimeEmissionsTile = {
  blurb: generateBlurb(
    "Emissions generated from fuel consumed while idling. This fuel consumption " +
      "is separate from active fuel consumption of the vehicle while driving. " +
      "These are fossil-fuel CO₂ emissions only and do not include CH₄ or N₂O " +
      "emissions. Vehicle-level biogenic emissions are reported separately under " +
      "Biogenic Emissions in the report and are not included in this calculation."
  ),
  legend: "",
};

export const driveStatusVehicleProfile = {
  blurb: generateBlurb(
    "Indicates whether a truck is active or not on a specific day."
  ),
  legend: "",
};

export const driveIdleRatioVehicleProfile = {
  blurb: generateBlurb(
    "The percentage of time a truck spends driving compared to idling."
  ),
  legend: "",
};

export const daysActiveVehicleProfile = {
  blurb: generateBlurb(
    "The number of days a truck is driven during a given month."
  ),
  legend: "",
};
